import {
    Checkbox,
    FormControl,
    FormControlLabel,
    FormGroup,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
} from "@mui/material";
import axios from "../../../utils/axiosInstance";
import { useSnackbar } from "notistack";
import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useState,
} from "react";
import { useNavigate } from "react-router";
import { MILLIS_TO_LOCALE } from "../../../utilities/helpers/formatUnix";
import { INGESTION_STATUS } from "../LandingPage/constants";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

export const PACKAGE_LOG_TYPE = "XML_GENERATION_LOG";
export const VENDOR_TRANSFER_LOG_TYPE = "VENDOR_TRANSFER_LOG";

export const PACKAGE_VENDOR_OPTIONS = ["PVC", "Roku", "MDU"] as const;
export const TRANSFER_VENDOR_OPTIONS = ["PVC", "Roku"] as const;

// Older rows have no startedAt; the stream name ends with the start time
// (ISO timestamp with ':' replaced by '.').
const startedAtMillis = (row: any) => {
    if (row?.startedAt) return row.startedAt;
    const match = String(row?.streamName || "").match(
        /(\d{4}-\d{2}-\d{2}T\d{2}\.\d{2}\.\d{2}\.\d{3}Z)$/
    );
    if (!match) return null;
    const iso = match[1].replace(
        /T(\d{2})\.(\d{2})\.(\d{2}\.\d{3}Z)$/,
        "T$1:$2:$3"
    );
    const millis = Date.parse(iso);
    return Number.isNaN(millis) ? null : millis;
};

// Pre-Roku package rows have no distributor/vendor on the Dynamo item.
const rowTypeLabel = (row: any) =>
    row?.distributor || row?.vendor || "—";

type PackageLogsTableProps = {
    handleLoader: (value: boolean) => void;
    logType?: string;
    detailPath?: string;
    vendorOptions?: readonly string[];
};

const PVCPackageLogsTable = forwardRef(
    (
        {
            handleLoader,
            logType = PACKAGE_LOG_TYPE,
            detailPath = "/pvc-package-logs",
            vendorOptions = PACKAGE_VENDOR_OPTIONS,
        }: PackageLogsTableProps,
        ref: any
    ) => {
        const navigate = useNavigate();
        const { enqueueSnackbar } = useSnackbar();
        const [ingestionList, setIngestionLogsList] = useState<any>([]);
        const [page, setPage] = useState(0);
        const [rowsPerPage, setRowsPerPage] = useState(10);
        const [totalCount, setTotalCount] = useState(0);
        const [skipKeyQueue, setskipKeyQueue] = useState<any>([]);
        const [filterOptions, setFilterOptions] = useState<any>({
            status: [...INGESTION_STATUS.map((item) => item.key)],
            completedAfter: null,
            completedBefore: null,
            vendor: "",
        });

        const onFilterOptionsChange = (filter: any) => {
            if (filter.hasOwnProperty("status")) {
                if (filterOptions.status.includes(filter.status)) {
                    filter.status = filterOptions.status.filter(
                        (status: string) => status != filter.status
                    );
                } else {
                    filter.status = [...filterOptions.status, filter.status];
                }
            }
            const updatedFilter = { ...filterOptions, ...filter };
            setFilterOptions(updatedFilter);
            getIngestionLogsList({
                ...updatedFilter,
                status: updatedFilter.status,
            });
        };

        useImperativeHandle(
            ref,
            () => ({
                getIngestionLogsList,
            }),
            [filterOptions, logType, rowsPerPage]
        );

        useEffect(() => {
            getIngestionLogsList();
        }, [logType]);

        const resetTableMeta = () => {
            setskipKeyQueue([]);
            setPage(0);
        };

        const navigateToLogs = (log: any) => {
            const params = new URLSearchParams({
                ingestionId: String(log.id ?? ""),
                logStream: String(log.streamName ?? ""),
                ingestionName: `${log.studioName ?? ""}/${
                    log.ingestionName ?? ""
                }`,
                ingestionTime: String(log.lastModified ?? ""),
                executionId: String(log.executionId ?? ""),
                logType,
            });
            navigate(`${detailPath}?${params.toString()}`);
        };

        const getIngestionLogsList = async (filters = filterOptions) => {
            resetTableMeta();
            const pages = await getIngestionLogs(
                rowsPerPage,
                undefined,
                undefined,
                filters
            );
            if (pages) {
                setTotalCount(pages.totalCount);
                setIngestionLogsList([...pages.list]);
            }
        };

        const getIngestionLogs = async (
            limit: number | string = rowsPerPage || 0,
            skip: string = "",
            skipQueue: any[] | null = skipKeyQueue,
            filters = filterOptions
        ) => {
            const logs = await getLogsAPI(limit, skip, filters);
            if (logs) {
                const list = logs?.list;
                list.length &&
                    pushKey(
                        `${list[list.length - 1]?.id}__${
                            list[list.length - 1]?.lastModified
                        }`,
                        skipQueue
                    );
                return logs;
            }
            return null;
        };

        const getLogsAPI = async (
            limit: number | string,
            skip: string = "",
            filters: any = null
        ) => {
            try {
                if (filters?.status.length == 0)
                    filters.status = [
                        ...INGESTION_STATUS.map((item) => item.key),
                    ];
                handleLoader(true);
                const vendorParam = filters?.vendor
                    ? `&vendor=${encodeURIComponent(filters.vendor)}`
                    : "";
                const url = `${
                    process.env.BASE_URL
                }api/v1/cms/ingest/all?logType=${encodeURIComponent(
                    logType
                )}&limit=${limit}&skip=${skip}&status=${
                    filters?.status.join(",") || ""
                }&completedAfter=${
                    filters?.completedAfter || ""
                }&completedBefore=${
                    filters?.completedBefore || ""
                }${vendorParam}`;
                const saveVersion = await axios.get(url);
                return saveVersion.data?.data;
            } catch (error: any) {
                console.log(
                    "Error in getting master information",
                    error.response
                );
                if (error.response?.data?.message) {
                    enqueueSnackbar(error.response?.data?.message, {
                        variant: "warning",
                    });
                } else {
                    enqueueSnackbar(
                        "API failed with error : " +
                            error.response?.statusText,
                        { variant: "warning" }
                    );
                }
                return null;
            } finally {
                handleLoader(false);
            }
        };

        const tableRowRender = () => {
            return (
                <>
                    <TableRow sx={{ height: 20 }}>
                        <TableCell
                            className="border-none pt-0 pd-0"
                            align="right"
                        >
                            <h3>#</h3>
                        </TableCell>
                        <TableCell className="border-none pt-0 pd-0">
                            <h3>Studio name/ File name</h3>
                        </TableCell>
                        <TableCell className="border-none pt-0 pd-0">
                            <h3>Type</h3>
                        </TableCell>
                        <TableCell className="border-none pt-0 pd-0">
                            <h3>Execution ID</h3>
                        </TableCell>
                        <TableCell
                            className="border-none pt-0 pd-0"
                            width={180}
                            align="center"
                        >
                            <h3>Status</h3>
                        </TableCell>
                        <TableCell
                            className="border-none pt-0 pd-0"
                            align="right"
                        >
                            <h3>Total Rows</h3>
                        </TableCell>
                        <TableCell
                            className="border-none pt-0 pd-0"
                            align="right"
                        >
                            <h3>Passed</h3>
                        </TableCell>
                        <TableCell
                            className="border-none pt-0 pd-0"
                            align="right"
                        >
                            <h3>Failed</h3>
                        </TableCell>
                        <TableCell
                            width={200}
                            className="border-none pt-0 pd-0"
                            align="right"
                        >
                            <h3>Started At</h3>
                        </TableCell>
                        <TableCell
                            width={200}
                            className="border-none pt-0 pd-0"
                            align="right"
                        >
                            <h3>Completed At</h3>
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell className="pt-0" align="right"></TableCell>
                        <TableCell className="pt-0"></TableCell>
                        <TableCell className="pt-0">
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                                <InputLabel id="vendor-type-filter-label">
                                    Type
                                </InputLabel>
                                <Select
                                    labelId="vendor-type-filter-label"
                                    label="Type"
                                    value={filterOptions.vendor || ""}
                                    onChange={(e) =>
                                        onFilterOptionsChange({
                                            vendor: e.target.value,
                                        })
                                    }
                                >
                                    <MenuItem value="">All</MenuItem>
                                    {vendorOptions.map((option) => (
                                        <MenuItem key={option} value={option}>
                                            {option}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </TableCell>
                        <TableCell className="pt-0"></TableCell>
                        <TableCell className="pt-0" align="right">
                            <FormControl component="fieldset">
                                <FormGroup>
                                    {INGESTION_STATUS.map((status: any) => {
                                        return (
                                            <FormControlLabel
                                                style={{ height: "30px" }}
                                                control={
                                                    <Checkbox
                                                        checked={filterOptions.status.includes(
                                                            status.key
                                                        )}
                                                        onChange={(e: any) =>
                                                            onFilterOptionsChange(
                                                                {
                                                                    status: e
                                                                        .target
                                                                        .value,
                                                                }
                                                            )
                                                        }
                                                        name={status.label}
                                                        value={status.key}
                                                    />
                                                }
                                                label={status.label}
                                            />
                                        );
                                    })}
                                </FormGroup>
                            </FormControl>
                        </TableCell>
                        <TableCell className="pt-0" align="right"></TableCell>
                        <TableCell className="pt-0" align="right"></TableCell>
                        <TableCell className="pt-0" align="right"></TableCell>
                        <TableCell className="pt-0" align="right"></TableCell>
                        <TableCell className="pt-0" align="right">
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <div className="d-flex flex-column">
                                    <DatePicker
                                        label="After"
                                        format="MMM DD,YYYY"
                                        value={
                                            filterOptions.completedAfter &&
                                            dayjs(filterOptions.completedAfter)
                                        }
                                        onChange={(e: any) =>
                                            onFilterOptionsChange({
                                                completedAfter: new Date(
                                                    e?.$d
                                                ).getTime(),
                                            })
                                        }
                                        sx={{
                                            container: { width: 200 },
                                            input: { height: 10 },
                                            icon: { marginRight: 10 },
                                        }}
                                    />
                                    <DatePicker
                                        className="mt-1"
                                        label="Before"
                                        format="MMM DD,YYYY"
                                        value={
                                            filterOptions.completedBefore &&
                                            dayjs(
                                                filterOptions.completedBefore
                                            )
                                        }
                                        onChange={(e: any) =>
                                            onFilterOptionsChange({
                                                completedBefore: new Date(
                                                    e?.$d
                                                ).getTime(),
                                            })
                                        }
                                        sx={{
                                            container: { width: 200 },
                                            input: { height: 10 },
                                            icon: { marginRight: 10 },
                                        }}
                                    />
                                </div>
                            </LocalizationProvider>
                        </TableCell>
                    </TableRow>
                </>
            );
        };

        const handleChangePage = async (event: unknown, newPage: number) => {
            let skipKeyValue = "";
            let queue;
            if (newPage < page) {
                queue = popTwice();
                setskipKeyQueue([...queue]);
                skipKeyValue = queue[queue.length - 1] || "";
            } else {
                skipKeyValue = skipKeyQueue[skipKeyQueue.length - 1];
            }
            setPage(newPage);
            const pages = await getIngestionLogs(
                rowsPerPage,
                skipKeyValue,
                queue
            );
            setIngestionLogsList([...pages.list]);
            setTotalCount(pages.totalCount);
        };

        const handleChangeRowsPerPage = async (
            event: React.ChangeEvent<HTMLInputElement>
        ) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
            setskipKeyQueue([]);
            const pages = await getIngestionLogs(event.target.value);
            setIngestionLogsList([...pages.list]);
            setTotalCount(pages.totalCount);
        };

        const pushKey = (key: any, queue: any = skipKeyQueue) => {
            const skipValue = [...queue, key];
            setskipKeyQueue(skipValue);
        };

        const popKey = (queue: any[] = skipKeyQueue) => {
            const skipValue = [...queue];
            skipValue.pop();
            setskipKeyQueue(skipValue);
            return skipValue;
        };

        const popTwice = () => {
            const queue1 = popKey();
            const queue = popKey(queue1);
            return queue;
        };

        return (
            <>
                <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 650 }} aria-label="simple table">
                        <TableHead>{tableRowRender()}</TableHead>
                        <TableBody>
                            {ingestionList &&
                                ingestionList.map(
                                    (row: any, index: number) => (
                                        <TableRow
                                            key={row.id || index}
                                            sx={{
                                                "&:last-child td, &:last-child th":
                                                    {
                                                        border: 0,
                                                    },
                                            }}
                                        >
                                            <TableCell align="right">
                                                {index + 1}
                                            </TableCell>
                                            <TableCell
                                                className="cursor-pointer"
                                                onClick={() =>
                                                    navigateToLogs(row)
                                                }
                                                component="th"
                                                scope="row"
                                            >
                                                {row?.studioName || ""}/
                                                {row.ingestionName}
                                            </TableCell>
                                            <TableCell>
                                                {rowTypeLabel(row)}
                                            </TableCell>
                                            <TableCell>
                                                {row?.executionId ? (
                                                    <Tooltip
                                                        title={
                                                            row.executionArn ||
                                                            row.executionId
                                                        }
                                                    >
                                                        <span>
                                                            {row.executionId}
                                                        </span>
                                                    </Tooltip>
                                                ) : (
                                                    "-"
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                {row?.ingestionStatus}
                                            </TableCell>
                                            <TableCell align="right">
                                                {row?.totalRows || "-"}
                                            </TableCell>
                                            <TableCell
                                                className="text-success"
                                                align="right"
                                            >
                                                {row?.passed || "-"}
                                            </TableCell>
                                            <TableCell
                                                className="text-danger"
                                                align="right"
                                            >
                                                {row?.failed || "-"}
                                            </TableCell>
                                            <TableCell align="right">
                                                {MILLIS_TO_LOCALE(
                                                    startedAtMillis(row)
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                {row?.ingestionStatus ==
                                                "COMPLETED"
                                                    ? MILLIS_TO_LOCALE(
                                                          row.lastModified
                                                      )
                                                    : "-"}
                                            </TableCell>
                                        </TableRow>
                                    )
                                )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </>
        );
    }
);

export default PVCPackageLogsTable;
